import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { toast as sonnerToast } from "sonner";
import "@/index.css";
import App from "@/App";
import {
  showApiErrorModal,
  shouldSuppressToast,
  extractServerMessage,
} from "@/lib/apiErrorModal";

/**
 * Suppress the benign "ResizeObserver loop completed with undelivered
 * notifications" errors emitted by browsers when a ResizeObserver
 * callback triggers layout changes faster than the browser can flush
 * them within one frame.
 *
 * Radix popovers, cmdk / combobox, and shadcn's Select all rely on
 * ResizeObserver internally. Rapid open→pick→close sequences (e.g.
 * choosing an account in the voucher grid) reliably emit this warning
 * on Chromium — cosmetic only, but CRA's dev-server error overlay
 * treats it as a fatal "Uncaught runtime error".
 *
 * Two-layer defence:
 *  1. Wrap `ResizeObserver` so every callback runs inside
 *     `requestAnimationFrame`. This defers layout reads to the next
 *     frame, which prevents the "loop completed" condition from ever
 *     firing in the first place.
 *  2. As a belt-and-braces safety net, install `window.error` /
 *     `unhandledrejection` listeners in the capture phase that
 *     swallow just this specific message — this catches any residual
 *     firings from libraries that hold their own ResizeObserver refs.
 */
if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function') {
  const NativeRO = window.ResizeObserver;
  class DeferredResizeObserver extends NativeRO {
    constructor(callback) {
      super((entries, observer) => {
        // Batch into the next animation frame. Cancels any prior
        // pending run so we always process the latest entries only.
        if (this._raf) window.cancelAnimationFrame(this._raf);
        this._raf = window.requestAnimationFrame(() => {
          this._raf = 0;
          try { callback(entries, observer); } catch (e) { /* swallow */ }
        });
      });
      this._raf = 0;
    }
    disconnect() {
      if (this._raf) window.cancelAnimationFrame(this._raf);
      this._raf = 0;
      super.disconnect();
    }
  }
  // Preserve the original name/prototype relationship so libraries
  // that instanceof-check ResizeObserver still work.
  Object.defineProperty(DeferredResizeObserver, 'name', { value: 'ResizeObserver' });
  window.ResizeObserver = DeferredResizeObserver;
}

const RESIZE_OBSERVER_ERR = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (e?.message && RESIZE_OBSERVER_ERR.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
    return undefined;
  }, true /* capture — beat CRA's dev-server overlay to the punch */);
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e?.reason?.message || String(e?.reason || '');
    if (RESIZE_OBSERVER_ERR.test(msg)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}

const queryClient = new QueryClient({
  /*
   * Central Action-API Error Modal (PRD Universality Rule #15)
   * ---------------------------------------------------------
   * React-Query separates "action" APIs (`useMutation` — Save,
   * Delete, Confirm, …) from "background" APIs (`useQuery` —
   * list, search, master lookups). Mutations always originate
   * from an operator gesture, so their failures deserve a
   * blocking, copy-friendly modal. Query failures stay on the
   * existing toast layer.
   *
   * The `MutationCache.onError` callback fires exactly ONCE per
   * mutation, BEFORE any per-mutation `onError`. We use it to
   * dispatch the global modal event.
   */
  mutationCache: new MutationCache({
    onError: (err) => {
      showApiErrorModal(extractServerMessage(err));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

/*
 * Monkey-patch `sonner.toast.error` so any legacy page-level
 * `toast.error(err.message)` firing the SAME text right after the
 * modal is silently swallowed. Other toast.error calls (form
 * validation, list-search failures, non-mutation errors) still
 * render normally.
 */
if (typeof sonnerToast?.error === 'function' && !sonnerToast.__chanakya_wrapped__) {
  const origError = sonnerToast.error.bind(sonnerToast);
  sonnerToast.error = (message, options) => {
    const text = typeof message === 'string'
      ? message
      : String(message?.message || message || '');
    if (shouldSuppressToast(text)) return undefined;
    return origError(message, options);
  };
  sonnerToast.__chanakya_wrapped__ = true;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
